import type * as Sentry from '@sentry/react';
import { ThemeProvider } from 'next-themes';
import { createRoot } from 'react-dom/client';

import App from './App.tsx';
import './index.css';
import {
  applyLocaleOverrides,
  applyPrerenderedOverrides,
  changeAppLanguage,
  i18nReady,
  isAppLanguage,
  type AppLanguage,
  type LocaleResources,
} from './i18n';
import { apiClient } from '@/lib/api/client';
import { getCachedDraftOverrides } from '@/lib/i18n-draft-cache';

// Suppress development warnings and browser extension noise
import './lib/utils/suppressWarnings';

let sentryRuntimePromise: Promise<typeof Sentry> | null = null;
let sentryInitializationPromise: Promise<void> | null = null;

function loadSentryRuntime(): Promise<typeof Sentry> {
  sentryRuntimePromise ??= import('@sentry/react');
  return sentryRuntimePromise;
}

function normalizeError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

function reportBootstrapError(context: string, error: unknown): void {
  const normalizedError = normalizeError(error);
  console.error(`[i18n bootstrap] ${context}`, normalizedError);
  void captureBootstrapException(normalizedError, context);
}

async function captureBootstrapException(error: Error, context: string): Promise<void> {
  try {
    if (sentryInitializationPromise !== null) {
      await sentryInitializationPromise;
    }
    const sentry = await loadSentryRuntime();
    sentry.captureException(error, {
      tags: { subsystem: 'i18n-bootstrap', context },
    });
  } catch (monitoringError) {
    console.error('[monitoring] Sentry could not capture the i18n bootstrap error', normalizeError(monitoringError));
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parsePlatformLanguage(value: unknown): AppLanguage {
  if (!isRecord(value) || !isAppLanguage(value.language)) {
    throw new TypeError('Platform language response contains an unsupported language');
  }
  return value.language;
}

function parseOverrideVersion(value: unknown): number {
  if (
    !isRecord(value) ||
    typeof value.version !== 'number' ||
    !Number.isSafeInteger(value.version) ||
    value.version < 0
  ) {
    throw new TypeError('Override version response must contain a non-negative integer');
  }
  return value.version;
}

function parseOverrideResources(value: unknown): LocaleResources {
  if (!isRecord(value) || !isRecord(value.resources)) {
    throw new TypeError('Override response must contain a resources object');
  }

  const resources: LocaleResources = {};
  for (const [namespace, translations] of Object.entries(value.resources)) {
    if (!isRecord(translations)) {
      throw new TypeError(`Override namespace ${namespace} must be an object`);
    }
    resources[namespace] = translations;
  }
  return resources;
}

async function fetchPlatformLanguage(): Promise<AppLanguage> {
  const response = await apiClient.get<unknown>('/i18n/character-attributes/translations');
  return parsePlatformLanguage(response.data);
}

async function fetchOverrideVersion(): Promise<number> {
  const response = await apiClient.get<unknown>('/i18n/overrides/version', {
    // This request must bypass browser caches so newly published copy is visible.
    params: { _t: Date.now() },
  });
  return parseOverrideVersion(response.data);
}

async function fetchPublishedOverrides(language: AppLanguage, version: number): Promise<LocaleResources> {
  const response = await apiClient.get<unknown>(`/i18n/overrides/${language}`, {
    params: { v: version },
  });
  return parseOverrideResources(response.data);
}

async function hydrateAppI18n(): Promise<void> {
  const [languageResult, versionResult] = await Promise.allSettled([fetchPlatformLanguage(), fetchOverrideVersion()]);

  let language: AppLanguage = 'de';
  if (languageResult.status === 'fulfilled') {
    language = languageResult.value;
    try {
      await changeAppLanguage(language);
    } catch (error) {
      reportBootstrapError(`Could not activate locale ${language}`, error);
      return;
    }
  } else {
    reportBootstrapError('Could not load platform language; German base locale stays active', languageResult.reason);
  }

  try {
    await applyPrerenderedOverrides(language);
  } catch (error) {
    reportBootstrapError(`Could not apply prerendered overrides for ${language}`, error);
  }

  if (versionResult.status === 'rejected') {
    reportBootstrapError('Could not load published override version', versionResult.reason);
  } else if (versionResult.value > 0) {
    try {
      const overrides = await fetchPublishedOverrides(language, versionResult.value);
      applyLocaleOverrides(language, overrides);
    } catch (error) {
      reportBootstrapError(`Could not load published overrides for ${language}`, error);
    }
  }

  const cachedDrafts = getCachedDraftOverrides(language);
  if (Object.keys(cachedDrafts).length > 0) {
    applyLocaleOverrides(language, cachedDrafts);
  }
}

const sentryDsn = import.meta.env.VITE_SENTRY_DSN as unknown;
const sentrySampleRate = import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE as unknown;

function parseSentrySampleRate(value: unknown): number {
  if (value === undefined || value === '') return 0;
  if (typeof value !== 'string') {
    throw new TypeError('VITE_SENTRY_TRACES_SAMPLE_RATE must be a number between 0 and 1');
  }

  const sampleRate = Number(value);
  if (!Number.isFinite(sampleRate) || sampleRate < 0 || sampleRate > 1) {
    throw new RangeError('VITE_SENTRY_TRACES_SAMPLE_RATE must be between 0 and 1');
  }
  return sampleRate;
}

async function initializeSentry(dsn: string): Promise<void> {
  const sentry = await loadSentryRuntime();
  sentry.init({
    dsn,
    integrations: [sentry.browserTracingIntegration()],
    tracesSampleRate: parseSentrySampleRate(sentrySampleRate),
    environment: import.meta.env.MODE,
  });
  if (import.meta.env.DEV) {
    console.info('Sentry initialized in the frontend');
  }
}

// Monitoring must never delay the first render. The runtime is only requested
// when a DSN is configured or an error needs to be captured.
if (typeof sentryDsn === 'string' && sentryDsn.length > 0) {
  sentryInitializationPromise = initializeSentry(sentryDsn).catch((error: unknown) => {
    console.error('Sentry could not be initialized in the frontend', normalizeError(error));
  });
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('React root element #root is missing');
}
const appRoot = rootElement;

// Wait for the first-paint locale so a non-German instance renders in its own
// language from the first frame. A stalled locale chunk must never block the
// app, so cap the wait and fall back to the bundled German base if exceeded.
const FIRST_PAINT_LOCALE_TIMEOUT_MS = 4000;

async function waitForFirstPaintLocale(): Promise<void> {
  let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
  const guard = new Promise<void>((resolve) => {
    timeoutHandle = setTimeout(resolve, FIRST_PAINT_LOCALE_TIMEOUT_MS);
  });
  try {
    await Promise.race([i18nReady, guard]);
  } finally {
    if (timeoutHandle !== undefined) {
      clearTimeout(timeoutHandle);
    }
  }
}

async function bootstrapApp(): Promise<void> {
  await waitForFirstPaintLocale();

  createRoot(appRoot).render(
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
      <App />
    </ThemeProvider>
  );

  void hydrateAppI18n().catch((error: unknown) => {
    reportBootstrapError('Unexpected hydration failure', error);
  });
}

void bootstrapApp();
